import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sprout, Plus, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { PLANT_CATALOG, SOW_WINDOWS } from '../lib/data';
import { getClimate, sowStatus } from '../lib/climate';
import SpeciesIcon from './species/SpeciesIcon';

// Localised plant name (catalog covers da/en/de/sv/no; other locales fall back to en).
function plantName(p, locale) {
    const base = (locale || 'en').split('-')[0];
    return p.name?.[base] || p.name?.en || p.name?.da || p.id;
}

/**
 * Dashboard "What to sow now" widget — pure client-side selection, no LLM.
 * Picks plants whose region-adjusted sowing window is open now (or opening soon),
 * based on the user's latitude via climate.js. One tap adds the plant to the garden.
 */
export default function SowNowWidget({ onAdded }) {
    const { t, locale } = useI18n();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [addingId, setAddingId] = useState(null);
    const [doneIds, setDoneIds] = useState(() => new Set());

    const items = useMemo(() => {
        const climate = getClimate(user?.location?.lat);
        const now = [];
        const soon = [];
        for (const p of PLANT_CATALOG) {
            const win = SOW_WINDOWS[p.id];
            if (!win) continue;
            const status = sowStatus(win, climate);
            if (status === 'now') now.push(p);
            else if (status === 'soon') soon.push(p);
        }
        return [...now.map((p) => ({ p, status: 'now' })), ...soon.map((p) => ({ p, status: 'soon' }))].slice(0, 4);
    }, [user?.location?.lat]);

    const addPlant = async (p) => {
        if (addingId || doneIds.has(p.id)) return;
        setAddingId(p.id);
        try {
            const r = await api.post('/plants', {
                name: plantName(p, locale),
                species: p.id,
                emoji: p.emoji,
                stage: 'Seedling',
                water_interval_days: p.interval,
            });
            setDoneIds((prev) => new Set(prev).add(p.id));
            toast.success(`${r.data?.name || plantName(p, locale)} 🌱`);
            onAdded?.(r.data);
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (err?.response?.status === 402) {
                toast.error(typeof d === 'string' ? d : 'Plant limit reached. Upgrade to Premium.');
                navigate('/premium');
            } else {
                toast.error(typeof d === 'string' ? d : t('common.error'));
            }
        } finally {
            setAddingId(null);
        }
    };

    return (
        <section className="mt-8" data-testid="sow-now-widget">
            <div className="flex items-end justify-between mb-3">
                <div>
                    <h2 className="font-serif text-2xl text-brand-ink inline-flex items-center gap-2">
                        <Sprout size={20} strokeWidth={1.8} className="text-brand-green" /> {t('sow_now.title')}
                    </h2>
                    <p className="text-xs text-brand-ink3 mt-0.5">{t('sow_now.sub')}</p>
                </div>
                <button
                    data-testid="sow-now-see-garden"
                    onClick={() => navigate('/my-garden')}
                    className="text-xs text-brand-green hover:text-brand-greenDark font-medium uppercase tracking-widest"
                >
                    {t('nav.garden')} →
                </button>
            </div>

            {items.length === 0 ? (
                <div data-testid="sow-now-empty" className="bg-brand-card border border-brand-border rounded-2xl p-5 text-center text-sm text-brand-ink3">
                    {t('sow_now.empty')}
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-3">
                    {items.map(({ p, status }) => {
                        const added = doneIds.has(p.id);
                        const adding = addingId === p.id;
                        return (
                            <button
                                key={p.id}
                                data-testid={`sow-now-${p.id}`}
                                onClick={() => addPlant(p)}
                                disabled={adding || added}
                                className={`bg-brand-card border rounded-2xl p-4 text-left transition-all flex items-start gap-3 ${
                                    added ? 'border-brand-green/50 bg-brand-green/5' : 'border-brand-border hover:-translate-y-0.5 hover:shadow-sm hover:border-brand-green'
                                }`}
                            >
                                <SpeciesIcon kind="plant" id={p.id} emoji={p.emoji || '🌱'} name={plantName(p, locale)} size={44} />
                                <div className="flex-1 min-w-0">
                                    <div className="font-serif text-base text-brand-ink truncate">{plantName(p, locale)}</div>
                                    <span className={`inline-block mt-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${
                                        status === 'now'
                                            ? 'bg-brand-green/12 text-brand-green'
                                            : 'bg-brand-ochre/15 text-brand-ochre'
                                    }`}>
                                        {status === 'now' ? t('sow.now') : t('sow.soon')}
                                    </span>
                                </div>
                                {adding ? (
                                    <Loader2 size={16} className="text-brand-green flex-shrink-0 mt-0.5 animate-spin" />
                                ) : added ? (
                                    <Check size={16} strokeWidth={2.5} className="text-brand-green flex-shrink-0 mt-0.5" />
                                ) : (
                                    <Plus size={16} strokeWidth={2} className="text-brand-ink3 flex-shrink-0 mt-0.5" />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </section>
    );
}
